/**
 * BroadcastFlow Music Studio — Phase 1 UI.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────────┐
 *   │ Header                                                 │
 *   ├──────────────┬────────────────────────────────────────┤
 *   │ ConnectCard  │  PromptBuilder                          │
 *   │ Templates    │    ↓ "Compose with AI" → optimised prompt│
 *   │ Collections  │  RawPromptBox + Generate                │
 *   │              │  Library grid                           │
 *   └──────────────┴────────────────────────────────────────┘
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Music, Loader2, Plug, Unplug, Sparkles, Heart, Trash2, RefreshCw,
  Download, ExternalLink, Check, X, ChevronDown, ChevronUp, Plus,
  Library as LibraryIcon, Disc3, Radio, Mic, Megaphone, Video,
  Stethoscope,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import DiagnosticsPanel from "@/components/DiagnosticsPanel";
import { api } from "@/lib/api";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import { appConfirm } from "@/components/AppDialogHost";


export default function MusicStudio() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [collections, setCollections] = useState({ collections: [], favorite_count: 0 });
  const [templates, setTemplates] = useState([]);
  const [models, setModels] = useState([]);
  const [activeFilter, setActiveFilter] = useState({ kind: "all" });  // {kind:'all'|'favorite'|'collection', value?}

  const loadStatus = useCallback(async () => {
    try { const r = await api.get("/music-studio/status"); setStatus(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Music Studio not accessible on this plan."); setStatus({ connected: false }); }
    finally { setStatusLoading(false); }
  }, []);

  const loadTracks = useCallback(async () => {
    const params = {};
    if (activeFilter.kind === "favorite") params.favorite = true;
    if (activeFilter.kind === "collection") params.collection = activeFilter.value;
    try {
      const r = await api.get("/music-studio/tracks", { params });
      setTracks(r.data?.items || []);
    } catch { /* ignore */ }
  }, [activeFilter]);

  const loadCollections = useCallback(async () => {
    try { const r = await api.get("/music-studio/collections"); setCollections(r.data); }
    catch { /* ignore */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try { const r = await api.get("/music-studio/templates"); setTemplates(r.data?.templates || []); }
    catch { /* ignore */ }
  }, []);

  const loadModels = useCallback(async () => {
    try { const r = await api.get("/music-studio/models"); setModels(r.data || []); }
    catch { setModels([]); /* fine — only available when connected */ }
  }, []);

  useEffect(() => { loadStatus(); loadTemplates(); }, [loadStatus, loadTemplates]);
  useEffect(() => { if (status?.connected) { loadTracks(); loadCollections(); loadModels(); } }, [status?.connected, loadTracks, loadCollections, loadModels]);
  useEffect(() => { loadTracks(); }, [activeFilter, loadTracks]);

  return (
    <div className="space-y-6" data-testid="music-studio-page">
      <PageHeader
        icon={Music}
        title="AI Music Studio"
        subtitle="BroadcastFlow AI music production · Suno-powered · Phase 1"
      />

      {statusLoading ? (
        <div className="p-8 flex items-center gap-2 text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading Music Studio…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <aside className="lg:col-span-3 space-y-5">
            <ConnectionCard status={status} onReload={loadStatus} />
            {status?.connected && (
              <CollectionsCard
                collections={collections}
                activeFilter={activeFilter}
                onPick={setActiveFilter}
              />
            )}
            {/* Templates are always visible — they double as a teaser for
                disconnected users so they can see what the studio can do. */}
            <TemplatesCard templates={templates} connected={!!status?.connected} />
          </aside>

          <main className="lg:col-span-9 space-y-6">
            {status?.connected ? (
              <>
                <Composer status={status} models={models} onGenerated={() => { loadTracks(); loadStatus(); }} />
                <Library
                  tracks={tracks}
                  activeFilter={activeFilter}
                  onChange={() => { loadTracks(); loadCollections(); }}
                />
              </>
            ) : (
              <EmptyState />
            )}
          </main>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Connection card
// ──────────────────────────────────────────────────────────────
function ConnectionCard({ status, onReload }) {
  const [open, setOpen] = useState(!status?.connected);
  const [wrappers, setWrappers] = useState([]);
  const [form, setForm] = useState({
    base_url: "", api_key: "", provider_type: "self_hosted_gcui",
    generate_path: "", feed_path: "", default_model: "chirp-v3-5",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/music-studio/wrappers"); setWrappers(r.data?.wrappers || []); }
      catch { setWrappers([]); }
    })();
  }, []);

  const wrapper = wrappers.find((w) => w.id === form.provider_type);
  const needsKey = wrapper?.requires_key ?? true;

  const connect = async () => {
    if (!form.base_url) { toast.error("Base URL is required."); return; }
    if (needsKey && !form.api_key) { toast.error("This wrapper requires an API key."); return; }
    setConnecting(true);
    try {
      const r = await api.post("/music-studio/connect", form);
      // Surface auto-sanitization so users know we fixed their URL.
      if (r.data?.sanitized && r.data?.sanitize_hint) {
        toast.message("We cleaned up your Base URL", {
          description: `${r.data.sanitize_hint} Saved as: ${r.data.base_url}`,
          duration: 9000,
        });
      }
      // If we auto-upgraded their wrapper choice (e.g. "custom" → "sunoapi_org")
      // reflect that locally so future "Edit" shows the corrected value.
      if (r.data?.wrapper_type && r.data.wrapper_type !== form.provider_type) {
        setForm((f) => ({ ...f, provider_type: r.data.wrapper_type, base_url: r.data.base_url || f.base_url }));
      } else if (r.data?.base_url && r.data.base_url !== form.base_url) {
        setForm((f) => ({ ...f, base_url: r.data.base_url }));
      }
      if (r.data?.verified) toast.success("Suno connected and verified.");
      else toast.warning(r.data?.warning || "Saved — first generate will verify.", { duration: 7000 });
      setOpen(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed.");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!await appConfirm("Disconnect Suno? Your library is preserved.")) return;
    try { await api.post("/music-studio/disconnect"); toast.success("Disconnected."); setOpen(true); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed."); }
  };

  const runDiagnostics = async () => {
    setDiagBusy(true);
    setDiagResult(null);
    try {
      const r = await api.post("/music-studio/diagnose");
      setDiagResult(r.data);
      if (r.data?.ok) toast.success("All checks passed.");
      else toast.warning(r.data?.summary || "Diagnostics found an issue.", { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostics failed to run.");
    } finally { setDiagBusy(false); }
  };

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="connection-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Disc3 className="h-4 w-4 text-brand-amber" />
          <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber">// Suno</h3>
        </div>
        {status?.connected && (
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
            status.verified ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" :
            "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"
          }`} data-testid="connection-badge">
            {status.verified ? "VERIFIED" : "UNVERIFIED"}
          </span>
        )}
      </div>

      {status?.connected && !open && (
        <>
          <div className="text-xs text-ink-secondary mb-1">{status.wrapper_type || "—"}</div>
          <div className="text-[10px] font-mono text-ink-muted truncate" title={status.wrapper_base_url}>{status.wrapper_base_url}</div>
          {status.quota && (
            <div className="mt-2 text-[11px] text-ink-muted">
              <div className="flex items-center justify-between">
                <span>Monthly</span>
                <span className="font-mono">{status.quota.used_this_month} / {status.quota.monthly_limit}</span>
              </div>
              <div className="w-full bg-bg rounded-full h-1.5 mt-1 overflow-hidden">
                <div className="bg-brand-amber h-full" style={{ width: `${Math.min(100, (status.quota.used_this_month / Math.max(1, status.quota.monthly_limit)) * 100)}%` }} />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button onClick={() => setOpen(true)} data-testid="edit-connection-btn"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-line hover:border-brand-amber/40">
              Edit
            </button>
            <button onClick={runDiagnostics} disabled={diagBusy} data-testid="run-diagnostics-btn"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50">
              {diagBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Stethoscope className="h-3 w-3" />}
              Diagnose
            </button>
            <button onClick={disconnect} data-testid="disconnect-btn"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-brand-onair/40 text-brand-onair hover:bg-brand-onair/10">
              <Unplug className="h-3 w-3" /> Disconnect
            </button>
          </div>
          {diagResult && (
            <MusicDiagnosticsPanel result={diagResult} onClose={() => setDiagResult(null)} onResult={setDiagResult} />
          )}
        </>
      )}

      {open && (
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Wrapper</label>
            <select
              value={form.provider_type}
              onChange={(e) => setForm({ ...form, provider_type: e.target.value })}
              data-testid="wrapper-select"
              className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none"
            >
              {wrappers.map((w) => (<option key={w.id} value={w.id}>{w.label}</option>))}
            </select>
            {wrapper?.hint && <div className="text-[10px] text-ink-muted mt-1 leading-snug">{wrapper.hint}</div>}
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Base URL</label>
            <input
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder={wrapper?.base_url_example || "https://…"}
              data-testid="base-url-input"
              className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none font-mono"
            />
            {/^https?:\/\/docs\./i.test(form.base_url || "") && (
              <div
                data-testid="docs-url-warning"
                className="mt-1 text-[10px] text-yellow-300 leading-snug bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-1"
              >
                That looks like a <strong>docs</strong> URL. Use the API host instead
                (e.g. <code>https://api.sunoapi.org</code>) — we&apos;ll auto-fix it for you on save.
              </div>
            )}
            {/(\/generate|\/api\/|\/v1\/audios|\/suno-api)/i.test(form.base_url || "") && (
              <div
                data-testid="path-url-warning"
                className="mt-1 text-[10px] text-yellow-300 leading-snug bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-1"
              >
                Don&apos;t include the endpoint path here — we add it from the wrapper config.
                Just paste the host (e.g. <code>{wrapper?.base_url_example || "https://api.sunoapi.org"}</code>).
              </div>
            )}
          </div>
          {needsKey && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">API Key</label>
              <input
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="Bearer token / API key"
                data-testid="api-key-input"
                className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none font-mono"
              />
            </div>
          )}
          <button type="button" onClick={() => setShowAdvanced((x) => !x)}
            className="inline-flex items-center gap-1 text-[10px] text-ink-muted hover:text-brand-amber"
          >
            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Advanced
          </button>
          {showAdvanced && (
            <>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Generate path (optional)</label>
                <input value={form.generate_path} onChange={(e) => setForm({ ...form, generate_path: e.target.value })}
                  placeholder="auto" data-testid="generate-path-input"
                  className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Feed path (optional)</label>
                <input value={form.feed_path} onChange={(e) => setForm({ ...form, feed_path: e.target.value })}
                  placeholder="auto" data-testid="feed-path-input"
                  className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none font-mono" />
              </div>
            </>
          )}
          <button onClick={connect} disabled={connecting} data-testid="connect-btn"
            className="w-full inline-flex items-center justify-center gap-2 type-button-label px-3 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50 amber-glow mt-2">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
            {status?.connected ? "Update connection" : "Connect Suno"}
          </button>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Music Studio diagnostics — thin wrapper over the shared
// <DiagnosticsPanel/> that adds the Auto-fix footer button.
// ──────────────────────────────────────────────────────────────
const DIAG_STEP_LABELS = {
  credentials_loaded: "Credentials saved",
  base_url_reachable: "Base URL reachable",
  credit_probe: "Auth + credit probe",
  response_shape: "Provider speaks JSON",
  generate_path_ok: "Generate endpoint exists",
};

function MusicDiagnosticsPanel({ result, onClose, onResult }) {
  const [fixing, setFixing] = useState(false);
  const steps = Array.isArray(result?.steps) ? result.steps : [];
  const generateStepFailed = steps.find(
    (s) => s.name === "generate_path_ok" && s.ok === false
  );

  const runAutofix = async () => {
    setFixing(true);
    const tid = "autofix";
    toast.loading("Scanning known generate endpoints…", { id: tid });
    try {
      const r = await api.post("/music-studio/diagnose/autofix");
      if (r.data?.fix_applied) {
        toast.success(r.data.summary || "Endpoint fixed.", { id: tid });
        if (r.data?.diagnose) onResult(r.data.diagnose);
      } else {
        toast.warning(r.data?.summary || "Could not find a working endpoint.", { id: tid, duration: 9000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed.", { id: tid });
    } finally {
      setFixing(false);
    }
  };

  const footer = generateStepFailed ? (
    <button
      onClick={runAutofix}
      disabled={fixing}
      data-testid="diagnostics-autofix-btn"
      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-50"
    >
      {fixing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
      {fixing ? "Scanning…" : "Auto-fix endpoint"}
    </button>
  ) : null;

  return (
    <DiagnosticsPanel
      result={result}
      labels={DIAG_STEP_LABELS}
      onClose={onClose}
      footer={footer}
    />
  );
}


// ──────────────────────────────────────────────────────────────
// Collections sidebar
// ──────────────────────────────────────────────────────────────
function CollectionsCard({ collections, activeFilter, onPick }) {
  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="collections-card">
      <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber mb-2">// Library</h3>
      <div className="space-y-1">
        <FilterBtn
          label="All tracks" icon={LibraryIcon}
          active={activeFilter.kind === "all"}
          onClick={() => onPick({ kind: "all" })}
          testid="filter-all"
        />
        <FilterBtn
          label={`Favorites · ${collections.favorite_count || 0}`} icon={Heart}
          active={activeFilter.kind === "favorite"}
          onClick={() => onPick({ kind: "favorite" })}
          testid="filter-favorite"
        />
        {(collections.collections || []).map((c) => (
          <FilterBtn
            key={c.name}
            label={`${c.name} · ${c.count}`} icon={Disc3}
            active={activeFilter.kind === "collection" && activeFilter.value === c.name}
            onClick={() => onPick({ kind: "collection", value: c.name })}
            testid={`filter-collection-${c.name.replace(/[^A-Za-z0-9-]/g, "-")}`}
          />
        ))}
      </div>
    </div>
  );
}

function FilterBtn({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-xs ${
        active ? "bg-brand-amber/15 text-brand-amber" : "text-ink-secondary hover:bg-bg"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="truncate">{label}</span>
    </button>
  );
}


// ──────────────────────────────────────────────────────────────
// Templates sidebar
// ──────────────────────────────────────────────────────────────
function TemplatesCard({ templates, connected = true }) {
  const handleClick = (t) => {
    if (!connected) {
      toast.message("Connect Suno first to use one-tap generate.", {
        description: "Add your wrapper credentials in the connection card above.",
      });
      return;
    }
    window.dispatchEvent(new CustomEvent("music-template-generate", { detail: t }));
  };
  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="templates-card">
      <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber mb-2">// One-tap Templates</h3>
      <div className="text-[10px] text-ink-muted mb-2">
        {connected ? "Click to auto-compose AND generate instantly →" : "Connect Suno, then one-tap any preset →"}
      </div>
      <div className="space-y-1 max-h-72 overflow-auto pr-1">
        {templates.map((t) => (
          <button
            key={t.id}
            data-testid={`template-${t.id}`}
            onClick={() => handleClick(t)}
            className={`group w-full text-left text-[11px] px-2 py-1.5 rounded-md transition flex items-center justify-between gap-2 ${
              connected
                ? "text-ink-secondary hover:bg-brand-amber/10 hover:text-brand-amber"
                : "text-ink-muted hover:bg-bg hover:text-ink-secondary"
            }`}
          >
            <span className="truncate">{t.label}</span>
            <Sparkles className={`h-3 w-3 opacity-0 group-hover:opacity-100 transition shrink-0 ${connected ? "" : "text-ink-muted"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Composer — Prompt Builder + Generate
// ──────────────────────────────────────────────────────────────
// Maps the UI length picker to a hard target duration in seconds.
// When this returns a value ≤ 90s, the backend will trim the Suno output
// post-render with a smooth fade-out (because Suno V4.5 ignores duration hints).
const LENGTH_TO_TARGET_SEC = {
  jingle: 15,
  short: 30,
  bumper: 20,
  normal: 0,    // 0 = no trim
  extended: 0,
};

function targetSecondsFromLength(length) {
  const v = LENGTH_TO_TARGET_SEC[(length || "").toLowerCase()];
  return v && v > 0 ? v : null;
}


function Composer({ status, models, onGenerated }) {
  // Builder state
  const [b, setB] = useState({
    genre: "", mood: "", tempo: "medium", energy: "medium",
    vocal_style: "", instrumental: false, length: "normal",
    purpose: "", target_audience: "", notes: "",
  });
  const [composedPrompt, setComposedPrompt] = useState("");
  const [composedTitle, setComposedTitle] = useState("");
  const [composedTags, setComposedTags] = useState([]);
  const [composing, setComposing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [collection, setCollection] = useState("");
  const [model, setModel] = useState("chirp-v3-5");
  const [templateId, setTemplateId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  // Listen for template clicks from the sidebar
  useEffect(() => {
    const loadHandler = (e) => {
      const t = e.detail || {};
      const d = t.defaults || {};
      setB((prev) => ({
        ...prev,
        ...d,
        purpose: t.purpose || prev.purpose,
        instrumental: d.instrumental ?? false,
      }));
      setTemplateId(t.id || null);
      toast.message(`Template loaded: ${t.label}`);
    };
    const oneTapHandler = async (e) => {
      const t = e.detail || {};
      const d = t.defaults || {};
      const merged = {
        ...b,
        ...d,
        purpose: t.purpose || b.purpose,
        instrumental: d.instrumental ?? false,
      };
      setB(merged);
      setTemplateId(t.id || null);
      toast.loading(`Composing "${t.label}" prompt…`, { id: "one-tap" });
      try {
        const composed = await api.post("/music-studio/prompt-builder", merged);
        const prompt = composed.data?.prompt || "";
        const title = composed.data?.title || t.label;
        const tags = composed.data?.tags || [];
        setComposedPrompt(prompt);
        setComposedTitle(title);
        setComposedTags(tags);
        if (!prompt.trim()) throw new Error("Composer returned empty prompt");
        toast.loading("Generating music (30-60s)…", { id: "one-tap" });
        const r = await api.post("/music-studio/generate", {
          prompt, title,
          instrumental: merged.instrumental,
          tags, model,
          collection: t.label,
          template_id: t.id || null,
          target_duration_sec: targetSecondsFromLength(merged.length),
        });
        toast.success(`Started ${r.data?.count || 0} tracks for "${t.label}".`, { id: "one-tap" });
        onGenerated();
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || "One-tap generate failed.", { id: "one-tap" });
      }
    };
    window.addEventListener("music-template", loadHandler);
    window.addEventListener("music-template-generate", oneTapHandler);
    return () => {
      window.removeEventListener("music-template", loadHandler);
      window.removeEventListener("music-template-generate", oneTapHandler);
    };
  }, [b, model, onGenerated]);

  const compose = async () => {
    setComposing(true);
    try {
      const r = await api.post("/music-studio/prompt-builder", b);
      setComposedPrompt(r.data?.prompt || "");
      setComposedTitle(r.data?.title || "");
      setComposedTags(r.data?.tags || []);
      toast.success("Prompt composed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Compose failed."); }
    finally { setComposing(false); }
  };

  const generate = async () => {
    if (!composedPrompt.trim()) { toast.error("Compose the prompt first (or paste one)."); return; }
    setGenerating(true);
    toast.message("Generating music — Suno is rendering. This usually takes 30-60 seconds.", { duration: 10000 });
    try {
      const r = await api.post("/music-studio/generate", {
        prompt: composedPrompt,
        title: composedTitle,
        instrumental: b.instrumental,
        tags: composedTags,
        model,
        collection,
        template_id: templateId,
        target_duration_sec: targetSecondsFromLength(b.length),
      });
      const trimMsg = targetSecondsFromLength(b.length)
        ? ` Output will be auto-trimmed to ${targetSecondsFromLength(b.length)}s with a smooth fade-out.`
        : "";
      toast.success(
        `Started ${r.data?.count || 0} tracks. Refresh icons will appear on each card while they render.${trimMsg}`
      );
      onGenerated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); }
    finally { setGenerating(false); }
  };

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-5" data-testid="composer-card">
      <h2 className="type-section-heading text-ink-primary mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-amber" />
        Prompt Builder
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        <Field label="Purpose" testid="builder-purpose" value={b.purpose} onChange={(v) => setB({ ...b, purpose: v })} placeholder="e.g. Morning show theme" />
        <Field label="Genre" testid="builder-genre" value={b.genre} onChange={(v) => setB({ ...b, genre: v })} placeholder="e.g. reggae, lo-fi, EDM" />
        <Field label="Mood" testid="builder-mood" value={b.mood} onChange={(v) => setB({ ...b, mood: v })} placeholder="e.g. chill, exciting" />
        <SelectField label="Tempo" testid="builder-tempo" value={b.tempo} onChange={(v) => setB({ ...b, tempo: v })}
          options={[{value:"slow",label:"Slow"},{value:"medium",label:"Medium"},{value:"fast",label:"Fast"}]} />
        <SelectField label="Energy" testid="builder-energy" value={b.energy} onChange={(v) => setB({ ...b, energy: v })}
          options={[{value:"low",label:"Low"},{value:"medium",label:"Medium"},{value:"high",label:"High"}]} />
        <SelectField label="Length" testid="builder-length" value={b.length} onChange={(v) => setB({ ...b, length: v })}
          options={[{value:"jingle",label:"Jingle (15s)"},{value:"short",label:"Short (30s)"},{value:"normal",label:"Normal"},{value:"extended",label:"Extended (loop)"}]} />
        <SelectField label="Vocal" testid="builder-vocal" value={b.vocal_style} onChange={(v) => setB({ ...b, vocal_style: v })}
          options={[{value:"",label:"Any"},{value:"male",label:"Male"},{value:"female",label:"Female"},{value:"choir",label:"Choir"},{value:"spoken",label:"Spoken word"}]} />
        <Field label="Target audience" testid="builder-audience" value={b.target_audience} onChange={(v) => setB({ ...b, target_audience: v })} placeholder="e.g. 25-44 commuters" />
        <label className="inline-flex items-center gap-2 self-end mb-2">
          <input type="checkbox" checked={b.instrumental} onChange={(e) => setB({ ...b, instrumental: e.target.checked })} data-testid="builder-instrumental" className="accent-brand-amber" />
          <span className="text-xs">Instrumental (no vocals)</span>
        </label>
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Notes (optional)</label>
          <AiWriteButton onClick={() => setAiOpen(true)} testid="music-studio-ai-write" label="Write for me" size="xs" />
        </div>
        <textarea
          value={b.notes}
          onChange={(e) => setB({ ...b, notes: e.target.value })}
          rows={2}
          placeholder="Any specifics the AI should incorporate"
          data-testid="builder-notes"
          className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
        />
      </div>

      <button onClick={compose} disabled={composing} data-testid="compose-btn"
        className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber disabled:opacity-50 mb-3">
        {composing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        Compose with AI
      </button>

      <div>
        <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Prompt</label>
        <textarea
          value={composedPrompt}
          onChange={(e) => setComposedPrompt(e.target.value)}
          rows={3}
          placeholder="Click 'Compose with AI' or paste a Suno prompt here"
          data-testid="composed-prompt"
          className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono"
        />
        <div className="text-[10px] text-ink-muted text-right">{composedPrompt.length} / 2000</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
        <Field label="Title" testid="composed-title" value={composedTitle} onChange={setComposedTitle} placeholder="(auto)" />
        <Field label="Collection" testid="composed-collection" value={collection} onChange={setCollection} placeholder="e.g. Morning Show Pack" />
        <SelectField label="Model" testid="composed-model" value={model} onChange={setModel}
          options={(models || []).length ? models.map((m) => ({ value: m.id, label: m.label })) : [{value:"chirp-v3-5",label:"Chirp v3.5"}]} />
      </div>
      {composedTags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {composedTags.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-line text-ink-secondary">
              {t}
              <button onClick={() => setComposedTags(composedTags.filter((_, j) => j !== i))} className="text-ink-muted hover:text-brand-onair">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <button onClick={generate} disabled={generating || !composedPrompt.trim()} data-testid="generate-btn"
        className="mt-4 inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50 amber-glow">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Music className="h-4 w-4" />}
        Generate music
      </button>

      <AiWriteScriptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        defaultScriptType="custom"
        defaultTone="conversational"
        defaultLengthSec={30}
        initialTopic={[
          b.purpose && `Purpose: ${b.purpose}`,
          b.genre && `Genre: ${b.genre}`,
          b.mood && `Mood: ${b.mood}`,
          b.target_audience && `Audience: ${b.target_audience}`,
        ].filter(Boolean).join("\n")}
        hideTitle
        onApply={({ script }) => {
          setB((prev) => ({ ...prev, notes: script }));
          setAiOpen(false);
          toast.success("Notes drafted. Click Compose with AI to build the prompt.");
        }}
      />
    </div>
  );
}


function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</label>
      <input
        value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none"
      />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Library grid
// ──────────────────────────────────────────────────────────────
function Library({ tracks, activeFilter, onChange }) {
  const heading = useMemo(() => {
    if (activeFilter.kind === "favorite") return "Favorites";
    if (activeFilter.kind === "collection") return `Collection · ${activeFilter.value}`;
    return "All tracks";
  }, [activeFilter]);

  return (
    <div data-testid="library">
      <div className="flex items-center justify-between mb-2">
        <h2 className="type-section-heading text-ink-primary flex items-center gap-2">
          <LibraryIcon className="h-4 w-4 text-brand-amber" /> {heading}
        </h2>
        <span className="type-timestamp text-ink-muted">{tracks.length} {tracks.length === 1 ? "track" : "tracks"}</span>
      </div>
      {tracks.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-bg-elevated p-6 text-center type-body text-ink-muted">
          Nothing here yet — generate your first track above.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tracks.map((t) => <TrackCard key={t.track_id} track={t} onChange={onChange} />)}
        </div>
      )}
    </div>
  );
}


function TrackCard({ track, onChange }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null); // "imaging" | "commercial" | "podcast" | "vision" | null
  const navigate = useNavigate();

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/music-studio/tracks/${track.track_id}/refresh`);
      toast.success("Refreshed.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed."); }
    finally { setBusy(false); }
  };

  const toggleFav = async () => {
    try { await api.patch(`/music-studio/tracks/${track.track_id}`, { favorite: !track.favorite }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const remove = async () => {
    if (!await appConfirm(`Delete "${track.title}"?`)) return;
    try { await api.delete(`/music-studio/tracks/${track.track_id}`); toast.success("Deleted."); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const sendTo = async (destination, label) => {
    setSending(destination);
    const toastId = `send-${track.track_id}-${destination}`;
    toast.loading(`Sending to ${label}…`, { id: toastId });
    try {
      const r = await api.post(`/music-studio/tracks/${track.track_id}/send-to/${destination}`);
      const deeplink = r.data?.deeplink || null;
      toast.success(`Sent to ${label}. Opening…`, { id: toastId, action: deeplink ? {
        label: "Open",
        onClick: () => navigate(deeplink),
      } : undefined });
      if (deeplink) setTimeout(() => navigate(deeplink), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Send to ${label} failed.`, { id: toastId });
    } finally {
      setSending(null);
    }
  };

  const isReady = track.status === "complete" && track.audio_url;
  // Resolve a server-relative audio URL to an absolute one against the backend.
  const audioSrc = isReady
    ? (String(track.audio_url).startsWith("http")
        ? track.audio_url
        : `${process.env.REACT_APP_BACKEND_URL || ""}${track.audio_url}`)
    : null;

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-3" data-testid={`track-card-${track.track_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{track.title || "Untitled"}</div>
          <div className="text-[10px] font-mono text-ink-muted truncate flex items-center gap-1 flex-wrap">
            <span>{track.status}{track.model ? ` · ${track.model}` : ""}</span>
            {track.trimmed && track.target_duration_sec && (
              <span
                data-testid={`trimmed-badge-${track.track_id}`}
                title={
                  track.original_duration_sec
                    ? `Trimmed from ${Math.round(track.original_duration_sec)}s → ${track.target_duration_sec}s with fade-out`
                    : `Trimmed to ${track.target_duration_sec}s`
                }
                className="px-1.5 py-px rounded-full bg-brand-amber/15 border border-brand-amber/40 text-brand-amber text-[9px] uppercase tracking-wider"
              >
                ✂ {track.target_duration_sec}s
              </span>
            )}
          </div>
        </div>
        <button onClick={toggleFav} data-testid={`fav-${track.track_id}`}
          className={`p-1 rounded ${track.favorite ? "text-brand-amber" : "text-ink-muted hover:text-brand-amber"}`}>
          <Heart className={`h-4 w-4 ${track.favorite ? "fill-current" : ""}`} />
        </button>
      </div>
      {track.image_url && (
        <img src={track.image_url} alt="" className="w-full h-32 object-cover rounded mb-2" loading="lazy" />
      )}
      {isReady ? (
        <audio controls src={audioSrc} data-testid={`audio-${track.track_id}`} className="w-full" />
      ) : (
        <div className="text-[11px] text-ink-muted flex items-center gap-2 py-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Generating… (refresh to check)
        </div>
      )}
      {track.tags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {track.tags.slice(0, 4).map((tg, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded border border-line text-ink-muted">{tg}</span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1 mt-2">
        {!isReady && (
          <button onClick={refresh} disabled={busy} data-testid={`refresh-${track.track_id}`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-line hover:border-brand-amber/40">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
          </button>
        )}
        {isReady && (
          <a href={audioSrc} target="_blank" rel="noopener noreferrer" data-testid={`download-${track.track_id}`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-line hover:border-brand-amber/40">
            <Download className="h-3 w-3" /> MP3
          </a>
        )}
        <button onClick={remove} data-testid={`delete-${track.track_id}`}
          className="ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-line text-ink-muted hover:text-brand-onair hover:border-brand-onair/40">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {isReady && (
        <div className="mt-2 pt-2 border-t border-line/60" data-testid={`send-to-bar-${track.track_id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">// SEND TO</div>
            <UsageBadge track={track} />
          </div>
          <div className="grid grid-cols-4 gap-1">
            <SendToBtn icon={Radio} label="Imaging" count={track?.usage?.imaging || 0} active={sending === "imaging"} disabled={!!sending}
              onClick={() => sendTo("imaging", "Imaging Studio")} testid={`send-imaging-${track.track_id}`} />
            <SendToBtn icon={Megaphone} label="Spot" count={track?.usage?.commercial || 0} active={sending === "commercial"} disabled={!!sending}
              onClick={() => sendTo("commercial", "Commercial Creator")} testid={`send-commercial-${track.track_id}`} />
            <SendToBtn icon={Mic} label="Podcast" count={track?.usage?.podcast || 0} active={sending === "podcast"} disabled={!!sending}
              onClick={() => sendTo("podcast", "Podcasts")} testid={`send-podcast-${track.track_id}`} />
            <SendToBtn icon={Video} label="Vision" count={track?.usage?.vision || 0} active={sending === "vision"} disabled={!!sending}
              onClick={() => sendTo("vision", "Vision Studio")} testid={`send-vision-${track.track_id}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function UsageBadge({ track }) {
  const total = Number(track?.usage_total || 0);
  if (!total) {
    return (
      <span
        className="text-[8px] font-mono uppercase tracking-wider text-ink-muted/70"
        data-testid={`usage-badge-${track.track_id}`}
        title="This track hasn't been reused yet. Send it to another studio to start the counter."
      >
        unused
      </span>
    );
  }
  const u = track.usage || {};
  const breakdown = `Imaging ${u.imaging || 0} · Spot ${u.commercial || 0} · Podcast ${u.podcast || 0} · Vision ${u.vision || 0}`;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-brand-amber/40 bg-brand-amber/10 text-brand-amber"
      data-testid={`usage-badge-${track.track_id}`}
      data-total={total}
      title={breakdown}
    >
      <Sparkles className="h-2.5 w-2.5" />
      USED ×{total}
    </span>
  );
}

function SendToBtn({ icon: Icon, label, count = 0, active, disabled, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      title={count > 0 ? `Sent to ${label} ${count}× — click to send again` : `Send to ${label}`}
      className={`relative flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-md border text-[9px] font-medium transition ${
        active
          ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
          : count > 0
            ? "border-brand-amber/30 bg-brand-amber/5 text-brand-amber/90 hover:border-brand-amber/60 hover:bg-brand-amber/10"
            : "border-line text-ink-secondary hover:border-brand-amber/40 hover:text-brand-amber hover:bg-brand-amber/5"
      } disabled:opacity-50 disabled:cursor-not-allowed`}>
      {active ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      <span className="leading-none">{label}</span>
      {count > 0 && (
        <span
          data-testid={`${testid}-count`}
          className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-brand-amber text-bg text-[8px] font-bold leading-[14px] text-center"
        >
          {count}
        </span>
      )}
    </button>
  );
}


// ──────────────────────────────────────────────────────────────
// Empty state — when Suno isn't connected
// ──────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-line bg-bg-elevated p-12 text-center" data-testid="empty-state">
      <Music className="h-10 w-10 text-ink-muted mx-auto mb-3" />
      <div className="type-section-heading text-ink-primary mb-1">Connect Suno to start producing</div>
      <div className="type-body text-ink-muted max-w-md mx-auto">
        Suno has no official public API in 2026, so BroadcastFlow integrates via your choice of <b>third-party wrapper</b>:
        run the open-source <a href="https://github.com/gcui-art/suno-api" target="_blank" rel="noopener noreferrer" className="text-brand-amber hover:underline">gcui-art/suno-api</a> bridge for free,
        or use a commercial wrapper like SunoAPI.org, API.box, EvoLink or 302.ai.
        <br /><br />
        Pick your wrapper on the left, paste your base URL + key, and you&apos;re producing within seconds.
      </div>
    </div>
  );
}
